import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_continentsLow from "@amcharts/amcharts4-geodata/continentsLow";
import * as am4plugins_bullets from "@amcharts/amcharts4/plugins/bullets";

import { fetchThreatActors as fetchThreatActorsService} from 'services/modules/ecs/data';
import { fetchThreatActors as fetchRSThreatActorsService} from 'services/modules/rhymscan/data';
import { fetchLatestReportDate as fetchLatestReportDateService } from 'services/modules/rhymscan/report';
import { fetchThreatActorsById } from 'services/modules/vapt/data';

am4core.useTheme(am4themes_animated);

const ThreatActorGeography = () => {
  
  // Redux dispatch
  const dispatch = useDispatch();

  // useRef hook
  const chartDivRef = useRef(null);
  // State variable
  const [ecsApplication, setEcsApplication] = useState(false);
  const [rsApplication, setRSApplication] = useState(false);
  const [threatActors, setThreatActors] = useState([]);
  
  // Redux selectors
  const reportDateContainer = useSelector((state) => state.report.latestReportDate);
  const rsReportDateContainer = useSelector((state) => state.rsReport.latestReportDate);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData );
  const threatActorsContainer = useSelector(state => state.ecsData.threatActors);
  const rsThreatActorsContainer = useSelector(state => state.rsData.threatActors);
  const vaptThreatActorsContainer = useSelector(state => state.vaptData.threatActors);
  const authenticatedUserContainer = useSelector((state) => state.auth.user);

  const fetchVAPTThreatActorsData = async() => {
    let params = {
      "id":selectedCompanyContainer.companyid,
    }
    dispatch(fetchThreatActorsById({ params }));
  };

  useEffect(() => {
    if (selectedCompanyContainer) {
      setEcsApplication(false);
      setRSApplication(false);

      const fetchCompanyReport = async () => {
        const domain = selectedCompanyContainer.domain;
        let params = { domain: domain };
        dispatch(fetchLatestReportDateService({ params }));
      };

      if (selectedCompanyContainer?.companyid === authenticatedUserContainer?.company?.id) {
        authenticatedUserContainer?.company?.applications?.forEach(element => {
          if (element.slug === "crs") {
            setEcsApplication(true);
          }
          else if(element.slug === "vapt"){
            fetchVAPTThreatActorsData();
          }
          else if(element.slug === "rs"){
            setRSApplication(true);
          }
        });
        // In the other case it finds whether the customers have CRQ service or not
      } else {
        if (authenticatedUserContainer?.company?.customers?.length > 0) {
          const companyCustomers = authenticatedUserContainer.company.customers;
          const customersWithSameCompanyId = companyCustomers.filter(customer => customer.id === selectedCompanyContainer.companyid);
          if (customersWithSameCompanyId.length > 0) {
            customersWithSameCompanyId[0].applications.forEach(element => {
              if (element.slug === "crs") {
                setEcsApplication(true);
              }
              else if(element.slug === "vapt"){
                fetchVAPTThreatActorsData();
              }
              else if(element.slug === "rs"){
                setRSApplication(true);
              }
            });
          }
        }
      }

      fetchCompanyReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);
  
  // useEffect hook fetches the threat actors data based on domain and date
  useEffect(() => {
    const fetchThreatActorsData = async() => {
      let params = {
        "domain":selectedCompanyContainer.domain,
        "date":reportDateContainer
      }
      
      dispatch(fetchThreatActorsService({ params }));
    };
    if (ecsApplication && reportDateContainer){
      fetchThreatActorsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDateContainer]);

  // useEffect hook fetches the threat actors data based on domain and date from RHYMScan
  useEffect(() => {
    const fetchRSThreatActorsData = async() => {
      let params = {
        "domain":selectedCompanyContainer.domain,
        "date":rsReportDateContainer
      }
      
      dispatch(fetchRSThreatActorsService({ params }));
    };
    if (rsApplication && rsReportDateContainer){
      fetchRSThreatActorsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsReportDateContainer]);

  useEffect(() => {
    let combinedThreatActors = [];
    if(threatActorsContainer && rsThreatActorsContainer && vaptThreatActorsContainer){
      combinedThreatActors = [
        ...threatActorsContainer,
        ...rsThreatActorsContainer,
        ...vaptThreatActorsContainer
      ];
    }
    else if(threatActorsContainer){
      combinedThreatActors = [...threatActorsContainer];
    }
    else if(rsThreatActorsContainer){
      combinedThreatActors = [...rsThreatActorsContainer];
    }
    else if(vaptThreatActorsContainer){
      combinedThreatActors = [...vaptThreatActorsContainer];
    }
    if(combinedThreatActors){
      //removing duplicates from threatactors objects array
      const ids = combinedThreatActors.map(({ name }) => name);
      const filteredThreatActors = combinedThreatActors.filter(({ name }, index) =>
          !ids.includes(name, index + 1));
      const mergedThreatActors = Object.values(
        filteredThreatActors.reduce((acc, actor) => {
          // If the country already exists in the accumulator, merge the name
          if (acc[actor.country]) {
            acc[actor.country].name += `\n ${actor.name}`;
          } else {
            // If the country doesn't exist, add it with current actor details
            acc[actor.country] = { ...actor };
          }
          return acc;
        }, {})
      );
      
      setThreatActors(mergedThreatActors);
    }
  }, [threatActorsContainer, rsThreatActorsContainer, vaptThreatActorsContainer]);

  useEffect(() =>{
    const chart = am4core.create(chartDivRef.current, am4maps.MapChart);
    chart.data = threatActors;

    chart.geodata = am4geodata_continentsLow;
    chart.projection = new am4maps.projections.Miller();

    chart.homeGeoPoint = {
      latitude: 25,
      longitude: 0
    }

    // Disable zoom and pan
    chart.maxZoomLevel = 1;
    chart.seriesContainer.draggable = false;
    chart.seriesContainer.resizable = false;

    // Create map polygon series
    let polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.exclude = ["antarctica"];
    polygonSeries.useGeodata = true;

    // Configure series
    let polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.fill = am4core.color("#454a58");

    // Add shadow
    let shadow = polygonSeries.filters.push(new am4core.DropShadowFilter());
    shadow.color = am4core.color("#60666b");
    shadow.blur = 0;

    // Pins
    let imageSeries = chart.series.push(new am4maps.MapImageSeries());
    let imageTemplate = imageSeries.mapImages.template;
    imageTemplate.propertyFields.longitude = "longitude";
    imageTemplate.propertyFields.latitude = "latitude";
    imageTemplate.nonScaling = true;

    // Creating a pin bullet
    let pin = imageTemplate.createChild(am4plugins_bullets.PinBullet);

    // Configuring pin appearance
    pin.background.fill = am4core.color("#FF5C5C");
    pin.background.pointerBaseWidth = 1;
    pin.background.pointerLength = 20;
    pin.background.radius = 5;

    pin.circle.fill = pin.background.fill;
    pin.tooltipText  = '{name}';

    imageSeries.data = chart.data;

    // Cleanup function
    return () => {
      chart.dispose();
    };

  }, [threatActors]);

  return (
    <div className='integrated-dashboard-bg'>
      <p className="carousel-title">Threat Actor Location Overview</p>
      {threatActors.length >0 ? (
        <div>
          <div ref={chartDivRef} id="threat-chartdiv" className="threat-actor-data"></div>
        </div>
      ):(
        <div className="no-threatactors-data">
          <p className="carousel-no-data">No relevant threat actors found</p>
          <div ref={chartDivRef} id="threat-chartdiv" className="no-threat-data"></div>
        </div>
      )}


    </div>
  )
}

export default ThreatActorGeography;
